import { useState } from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, TrendingUp, FileText, MessageSquare } from 'lucide-react';
import Header from '../layout/Header';

const aiDemos = [
  {
    id: 1,
    title: "Analyse de Performance d'Équipe",
    icon: TrendingUp,
    description: "L'IA analyse les métriques d'équipe et identifie les opportunités d'amélioration",
    color: "bg-blue-500"
  },
  {
    id: 2,
    title: "Génération de Rapports Automatisés",
    icon: FileText,
    description: "Créez des rapports de projet professionnels en quelques secondes",
    color: "bg-purple-500"
  },
  {
    id: 3,
    title: "Assistant de Prise de Décision",
    icon: Brain,
    description: "Obtenez des recommandations basées sur les données pour vos décisions TI",
    color: "bg-green-500"
  },
  {
    id: 4,
    title: "Chatbot de Support Interne",
    icon: MessageSquare,
    description: "Répondez instantanément aux questions courantes de l'équipe",
    color: "bg-orange-500"
  }
];

export default function DemoAI() {
  const [activeDemo, setActiveDemo] = useState<number | null>(null);

  return (
    <div className="min-h-screen">
      <Header 
        title="Démo AI" 
        subtitle="Explorez les capacités de l'intelligence artificielle pour votre équipe TI"
      />
      
      <div className="px-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {aiDemos.map((demo, index) => {
            const Icon = demo.icon;
            return (
              <motion.div
                key={demo.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => setActiveDemo(demo.id)}
                className={`aikup-card cursor-pointer ${
                  activeDemo === demo.id ? 'ring-2 ring-bz-blue' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`${demo.color} p-3 rounded-lg text-white`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-bz-dark mb-2">
                      {demo.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{demo.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Interactive Demo Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-gradient-to-br from-bz-dark to-bz-blue rounded-xl p-8 text-white"
        >
          <div className="flex items-center gap-3 mb-6">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
              }}
            >
              <Zap className="w-8 h-8 text-yellow-400" />
            </motion.div>
            <h3 className="text-2xl font-bold">Zone de Démonstration Interactive</h3>
          </div>

          {activeDemo ? (
            <motion.div
              key={activeDemo}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h4 className="font-semibold mb-3">Résultats de l'analyse AI</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Précision du modèle</span>
                    <span className="font-bold">94.2%</span>
                  </div>
                  <div className="bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "94.2%" }}
                      transition={{ duration: 1 }}
                      className="bg-green-400 h-2 rounded-full"
                    />
                  </div>
                  
                  <div className="flex justify-between items-center mt-4">
                    <span>Temps de traitement</span>
                    <span className="font-bold">0.3s</span>
                  </div>
                  <div className="bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "15%" }}
                      transition={{ duration: 0.5 }}
                      className="bg-blue-400 h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h4 className="font-semibold mb-3">Recommandations AI</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    <span>Optimiser les processus de déploiement pour réduire les délais de 30%</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    <span>Augmenter l'automatisation des tests pour améliorer la qualité</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">✓</span>
                    <span>Former 3 membres de l'équipe sur les technologies cloud natives</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          ) : (
            <div className="text-center py-12 text-white/70">
              <Brain className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Sélectionnez une démo ci-dessus pour voir l'IA en action</p>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-600 mb-4">
            Intéressé à intégrer ces fonctionnalités dans votre flux de travail?
          </p>
          <button className="aikup-button">
            Planifier une consultation
          </button>
        </motion.div>
      </div>
    </div>
  );
}



